import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Clock, MessageCircle, FileCheck, Send, Paperclip, Briefcase, FileText, Scale, Home, Users, DollarSign, BookOpen, FolderOpen, Activity, ChevronRight } from 'lucide-react';
import { LegalHelpBox } from './LegalHelpBox';
import { DocumentHubSection } from './DocumentHubSection';
import { useNavigate } from 'react-router-dom';
export function ToolsSection() {
  const navigate = useNavigate();
  const handleToolAction = tool => {
    console.log(`Opening ${tool} tool`);
  };
  return <div className="flex flex-col gap-6" data-id="element-425">
      {/* Row 1: AI Copilot - Full Width */}
      <div className="w-full" data-id="element-426">
        <AICopilotCard data-id="element-427" />
      </div>

      {/* Row 2: Other tools - Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-428">
        <DocumentHubSection data-id="element-429" />
        <LegalHelpBox data-id="element-430" />
      </div>

      {/* Row 3: Legal Health Check CTA */}
      <div className="w-full bg-white border border-borderColor rounded-xl p-6 lg:p-8 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-431">
        <div className="flex items-start gap-4" data-id="element-432">
          <div className="p-3 bg-aliceBlue rounded-xl flex-shrink-0" data-id="element-433">
            <Activity className="w-8 h-8 text-violetBlue" data-id="element-434" />
          </div>
          <div data-id="element-435">
            <h3 className="text-xl font-bold text-primary mb-1" data-id="element-436">
              Legal Health Check
            </h3>
            <p className="text-sm text-secondary max-w-xl leading-relaxed" data-id="element-437">
              Identify legal gaps and get a personalized action plan in minutes.
              Protect your personal assets or business with a guided review.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto flex-shrink-0" data-id="element-438">
          <button onClick={() => navigate('/legal-health-check')} className="bg-white border-2 border-violetBlue text-violetBlue hover:bg-aliceBlue font-medium py-2.5 px-5 rounded-lg transition-colors flex items-center justify-center gap-2" data-id="element-439">
            Personal Check
          </button>
          <button onClick={() => navigate('/legal-health-check')} className="bg-violetBlue hover:bg-[#6844E8] text-white font-medium py-2.5 px-5 rounded-lg transition-colors flex items-center justify-center gap-2" data-id="element-440">
            Business Check
            <ChevronRight className="w-4 h-4" data-id="element-441" />
          </button>
        </div>
      </div>
    </div>;
}
function AICopilotCard() {
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const starterPrompts = ['Review a lease clause', 'Termination letter', 'Refund dispute', 'Incorporate a business', 'Employment contract question', 'Family matter basics'];
  // Auto-resize textarea based on content when focused
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea && isFocused) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
    } else if (textarea && !isFocused) {
      textarea.style.height = '52px';
    }
  }, [query, isFocused]);
  const categories = [{
    id: 'employment',
    label: 'Employment & HR',
    icon: Briefcase
  }, {
    id: 'contracts',
    label: 'Contracts',
    icon: FileText
  }, {
    id: 'compliance',
    label: 'Compliance',
    icon: Scale
  }, {
    id: 'real-estate',
    label: 'Real Estate',
    icon: Home
  }, {
    id: 'corporate',
    label: 'Corporate Law',
    icon: Users
  }, {
    id: 'finance',
    label: 'Finance',
    icon: DollarSign
  }];
  const handleSubmit = e => {
    e.preventDefault();
    if (query.trim()) {
      console.log('Submitting legal query:', query);
      setQuery('');
      setIsFocused(false);
    }
  };
  const handleStarterPromptClick = (prompt: string) => {
    setQuery(prompt);
    setIsFocused(true);
    textareaRef.current?.focus();
  };
  const handleAttachFile = () => {
    console.log('Opening file attachment dialog');
  };
  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategory(categoryId === selectedCategory ? null : categoryId);
    console.log('Selected category:', categoryId);
  };
  const handleFocus = () => {
    setIsFocused(true);
  };
  const handleBlur = () => {
    if (!query.trim()) {
      setIsFocused(false);
    }
  };
  return <div className="bg-white border border-borderColor rounded-xl p-4 sm:p-6 lg:p-8 xl:p-10 shadow-sm" data-id="element-442">
      {/* Header - Tighter spacing, left aligned */}
      <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-443">
        <img src="https://cdn.magicpatterns.com/uploads/frDxN6VkzqMSZkD4nJP5uk/Screenshot_2025-12-31_at_11.06.59_AM.png" alt="LawVo AI" className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0" data-id="element-444" />
        <div className="flex-1 min-w-0" data-id="element-445">
          <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-446">
            <h3 className="text-xl sm:text-2xl font-bold text-primary" data-id="element-447">
              LawVo AI
            </h3>
            <span className="px-2 py-0.5 bg-aliceBlue text-violetBlue text-xs font-medium rounded-full whitespace-nowrap" data-id="element-448">
              Canada-focused
            </span>
          </div>
          <p className="text-sm sm:text-base text-secondary mb-1" data-id="element-449">
            Legal help made simple
          </p>
        </div>
      </div>

      {/* Compact Prompt Bar */}
      <form onSubmit={handleSubmit} className="mb-3 sm:mb-4" data-id="element-450">
        <div className={`relative bg-aliceBlue rounded-xl border-2 transition-all duration-200 ${isFocused ? 'border-violetBlue shadow-lg shadow-purple-100 bg-white' : 'border-borderColor shadow-sm hover:border-disabled'}`} data-id="element-451">
          {/* Textarea - Full Width */}
          <div className={`px-3 sm:px-4 transition-all duration-200 ${isFocused ? 'pt-4 sm:pt-5' : 'py-3 sm:py-4'}`} data-id="element-452">
            <textarea ref={textareaRef} value={query} onChange={e => setQuery(e.target.value)} onFocus={handleFocus} onBlur={handleBlur} placeholder="Ask a question, upload a document, or pick a topic to start." className="w-full bg-transparent border-0 text-sm sm:text-base text-primary placeholder-secondary focus:outline-none resize-none" style={{
            height: isFocused ? 'auto' : '40px',
            minHeight: isFocused ? '81px' : '40px',
            maxHeight: '240px'
          }} rows={1} data-id="element-453" />
          </div>

          {/* Bottom Panel - Help notes on left, buttons on right */}
          {isFocused && <div className="px-3 sm:px-4 pb-3 sm:pb-4 pt-2 flex items-center justify-between border-t border-borderColor" data-id="element-454">
              <div className="items-center gap-2 sm:gap-4 text-[10px] sm:text-xs text-secondary " data-id="element-455">
                <span className="whitespace-nowrap" data-id="element-456">Press Enter to send</span>
                <span className="hidden sm:inline" data-id="element-457">•</span>
                <span className="hidden sm:inline whitespace-nowrap" data-id="element-458">
                  Shift+Enter for new line
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-459">
                <button type="button" onClick={handleAttachFile} className="text-disabled hover:text-violetBlue transition-colors flex-shrink-0" data-id="element-460">
                  <Paperclip className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-461" />
                </button>

                <button type="submit" disabled={!query.trim()} className={`p-2.5 sm:p-3 rounded-xl transition-all flex-shrink-0 shadow-md ${query.trim() ? 'bg-gradient-to-r from-violetBlue to-[#6844E8] text-white hover:from-[#6844E8] hover:to-[#5835D0] hover:shadow-lg hover:scale-105 active:scale-95' : 'bg-disabled text-white cursor-not-allowed shadow-none'}`} data-id="element-462">
                  <Send className="w-4 h-5 sm:w-6 sm:h-6" data-id="element-463" />
                </button>
              </div>
            </div>}
        </div>
      </form>

      {/* Starter Prompts */}
      <div className="mb-4 sm:mb-5" data-id="element-464">
        <div className="flex flex-wrap gap-2" data-id="element-465">
          {starterPrompts.slice(0, window.innerWidth < 640 ? 4 : starterPrompts.length).map(prompt => <button key={prompt} onClick={() => handleStarterPromptClick(prompt)} className="px-2.5 py-1.5 bg-white hover:bg-aliceBlue border border-borderColor hover:border-violetBlue rounded-lg text-xs text-secondary hover:text-violetBlue transition-all whitespace-nowrap" data-id="element-466">
                {prompt}
              </button>)}
        </div>
      </div>

      {/* Topic Chips */}
      <div className="mb-3 sm:mb-4" data-id="element-467"></div>

      {/* Disclaimer - Collapsible */}
      <div className="pt-2 sm:pt-3 border-t border-borderColor" data-id="element-468">
        <button onClick={() => setShowDisclaimer(!showDisclaimer)} className="text-xs text-secondary hover:text-primary transition-colors" data-id="element-469">
          {showDisclaimer ? '− Hide' : '+'} AI Disclaimer & Terms
        </button>
        {showDisclaimer && <p className="text-xs text-secondary mt-2 leading-relaxed" data-id="element-470">
            <strong data-id="element-471">Disclaimer:</strong> This response is generated by AI and is
            for informational purposes only. It may not be accurate or complete
            and does not constitute legal advice. Do not rely on it as a
            substitute for legal counsel. For more, see our AI Disclaimer in
            Terms of Use.
          </p>}
      </div>
    </div>;
}
function ToolCard({
  icon,
  title,
  description,
  featureDescription,
  descriptionIcon,
  descriptionIconText,
  borderColor,
  accentColor,
  textColor,
  buttonText,
  onAction
}) {
  const [inquiry, setInquiry] = useState('');
  const handleSubmit = e => {
    e.preventDefault();
    if (inquiry.trim()) {
      console.log('Submitting inquiry:', inquiry);
      onAction();
      setInquiry('');
    }
  };
  return <div className={`bg-white border ${borderColor} rounded-lg p-5`} data-id="element-472">
      <div className="flex items-start mb-3" data-id="element-473">
        <div className={`p-2 ${accentColor} rounded-lg mr-3`} data-id="element-474">{icon}</div>
        <div data-id="element-475">
          <h3 className="text-lg font-semibold text-primary" data-id="element-476">{title}</h3>
          <p className="text-sm text-secondary mb-1" data-id="element-477">{description}</p>
        </div>
      </div>

      <div className="mb-3" data-id="element-478">
        <p className="text-xs text-secondary leading-relaxed" data-id="element-479">
          Submit your legal question below, select a date and time you're
          available, and a legal expert will contact you to discuss your
          question.
        </p>
      </div>

      <div className="flex items-center text-sm text-secondary mb-4" data-id="element-480">
        {descriptionIcon}
        <span data-id="element-481">{descriptionIconText}</span>
      </div>

      <form onSubmit={handleSubmit} data-id="element-482">
        <textarea value={inquiry} onChange={e => setInquiry(e.target.value)} placeholder="Describe your legal question or concern..." rows={1} className="w-full bg-aliceBlue border border-borderColor rounded-lg p-3 text-sm text-primary placeholder-disabled focus:outline-none focus:border-violetBlue focus:ring-1 focus:ring-violetBlue/20 resize-none mb-3" data-id="element-483" />
        <button type="submit" className={`${textColor} border border-current hover:bg-aliceBlue transition-colors px-4 py-2 rounded-md font-medium text-sm cursor-pointer`} data-id="element-484">
          {buttonText}
        </button>
      </form>
    </div>;
}